/**
 * Datadog RUM: idempotent init (closure + `getInitConfiguration()` after SDK loads).
 *
 * Uses dynamic `import("@datadog/browser-rum")` so the SDK loads in a separate async chunk.
 * Call from a client `useEffect` with `void ensureDatadogRumInit()` (or `await` inside an async IIFE).
 */

const applicationId = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN;

export const ensureDatadogRumInit = (() => {
  let initialised = false;
  let inFlight: Promise<void> | null = null;

  return async function ensureDatadogRumInit(): Promise<void> {
    if (typeof window === "undefined" || !applicationId || !clientToken) {
      return;
    }
    if (initialised) {
      return;
    }
    if (inFlight) {
      await inFlight;
      return;
    }

    inFlight = (async () => {
      const isProd = process.env.ENVIRONMENT?.toLowerCase() === "production";
      try {
        const { datadogRum, DefaultPrivacyLevel } = await import(
          "@datadog/browser-rum"
        );

        if (initialised) {
          return;
        }
        if (datadogRum.getInitConfiguration()) {
          initialised = true;
          return;
        }

        try {
          datadogRum.init({
            applicationId,
            clientToken,
            site: process.env.NEXT_PUBLIC_DD_RUM_SITE ?? "datadoghq.com",
            service:
              process.env.NEXT_PUBLIC_DD_RUM_SERVICE ?? "auto-buy-sdui-frontend",
            env:
              process.env.NEXT_PUBLIC_DD_RUM_ENV ??
              process.env.ENVIRONMENT ??
              "development",
            sessionSampleRate: 20,
            sessionReplaySampleRate: 0,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            trackBfcacheViews: true,
            defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT
          });
          initialised = true;
        } catch (initErr) {
          if (!isProd) {
            console.error("[Datadog RUM] init failed", initErr);
          }
        }
      } catch (loadErr) {
        if (!isProd) {
          console.error("[Datadog RUM] failed to load SDK", loadErr);
        }
      } finally {
        inFlight = null;
      }
    })();

    await inFlight;
  };
})();
